import { useState, useEffect } from "react";
import axios from "axios";
import { RefreshCw, Trash2, Edit3, Save, X, FileText, Mail, Download, Eye, FileSpreadsheet, Plus, Link2, Copy, Check, RotateCw, Search } from "lucide-react";
import NewInvoiceForm from "./NewInvoiceForm";
import InvoiceCorrect from "./InvoiceCorrect";
import { openPdf, previewPdf } from "../utils/pdf";
import AdminPager, { usePagedList, StateChips } from "./AdminPager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("sk-SK", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const EDIT_FIELDS = [
  ["number", "Číslo faktúry"],
  ["buyer_name", "Odberateľ"],
  ["buyer_email", "E-mail"],
  ["buyer_address", "Adresa"],
  ["buyer_ico", "IČO / Dát. nar."],
  ["buyer_country", "Krajina"],
  ["buyer_vat_id", "IČ DPH odberateľa"],
  ["amount_total", "Suma spolu (€)"],
  ["status", "Stav"],
  ["note", "Poznámka"],
];

export default function AdminInvoices({ token }) {
  const [q, setQ] = useState("");
  const [st, setSt] = useState("");
  const list = usePagedList(`${API}/admin/invoices`, token, { q, state: st });
  const ss = list.state_stats || {};
  const stateOpts = [["", "Všetky"], ["unpaid", "Nezaplatené"], ["paid", "Uhradené"], ["cancelled", "Zrušené (opravené)"]]
    .map(([key, label]) => ({ key, label, count: key ? ss[key]?.count : ["unpaid", "paid", "cancelled"].reduce((n, k) => n + (ss[k]?.count || 0), 0), sum: key ? ss[key]?.sum : undefined }));
  const { items, loading, reload: fetchItems } = list;
  const [showNew, setShowNew] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [reportMsg, setReportMsg] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState("");
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const reconcile = async () => {
    setReconcileBusy(true); setReconcileMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/payments/reconcile`, {}, auth);
      setReconcileMsg(`Overené: ${data.checked} · dokončené a odoslané: ${data.finalized} · expirované: ${data.expired} · stále čakajú: ${data.still_pending}`);
      if (data.finalized > 0) fetchItems();
    } catch { setReconcileMsg("Overenie Stripe platieb zlyhalo."); }
    finally { setReconcileBusy(false); }
  };

  const sendReport = async () => {
    setReportBusy(true); setReportMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/accounting/send-report`, {}, auth);
      setReportMsg(`Prehľad odoslaný na ${data.to} — ${data.count} faktúr · ${Number(data.total).toFixed(2)} €`);
    } catch { setReportMsg("Odoslanie prehľadu zlyhalo."); }
    finally { setReportBusy(false); }
  };

  useEffect(() => {
    axios.get(`${API}/admin/stripe-status`, auth).then(({ data }) => setStripe(data)).catch(() => setStripe(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const remove = async (id) => {
    if (!window.confirm("Naozaj zmazať túto faktúru?")) return;
    try { await axios.delete(`${API}/admin/invoices/${id}`, auth); fetchItems(); }
    catch (e) { /* ignore */ }
  };

  const save = async (id, fields) => {
    try { await axios.put(`${API}/admin/invoices/${id}`, fields, auth); fetchItems(); return true; }
    catch (e) { alert("Chyba pri ukladaní."); return false; }
  };

  const totalSum = list.active_sum || 0;

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/invoices.csv`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "faktury.csv"; a.click();
    } catch { alert("Export zlyhal."); }
  };

  return (
    <div data-testid="admin-invoices">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-white/40 text-sm" data-testid="invoices-total">{list.active_count || 0} faktúr · spolu <span className="text-[#D4AF37]">{Number(totalSum).toFixed(2)} €</span>{list.cancelled_count > 0 && <span className="text-white/30"> · {list.cancelled_count} zrušené sa nerátajú</span>}{(q || st) ? <span className="text-white/30"> · nájdených {list.total}</span> : null}</p>
          {stripe && (
            <span data-testid="stripe-status-badge" title={stripe.mode === "live" ? "Stripe beží v ostrom (LIVE) režime — platby sú reálne." : stripe.is_placeholder ? "Stripe nie je nakonfigurovaný (placeholder kľúč)." : "Stripe beží v testovacom režime — platby nie sú reálne."}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${stripe.mode === "live" ? "bg-green-500/15 text-green-400 border border-green-500/30" : "bg-amber-500/15 text-amber-400 border border-amber-500/30"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${stripe.mode === "live" ? "bg-green-400" : "bg-amber-400"}`} />
              Stripe: {stripe.mode === "live" ? "LIVE" : stripe.is_placeholder ? "NENASTAVENÝ" : "TEST"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowNew(true)} data-testid="invoices-new"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors">
            <Plus size={15} /> Nová faktúra
          </button>
          <button onClick={exportCsv} data-testid="invoices-export-csv"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <FileSpreadsheet size={15} /> Export CSV
          </button>
          <button onClick={fetchItems} data-testid="invoices-refresh"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Obnoviť
          </button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 border border-[#D4AF37]/20 bg-[#0d0d0d] px-4 py-3" data-testid="accounting-report-bar">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-[#D4AF37]">Účtovníctvo:</span> prehľad uhradených faktúr sa posiela automaticky <strong className="text-white/70">1. a 15. v mesiaci</strong> na smrktamartin@gmail.com (CSV + PDF). Stripe úhrady sa označia samé, prevod označíte ručne ako „Uhradené".
        </p>
        <button onClick={sendReport} disabled={reportBusy} data-testid="send-accounting-report"
          className="inline-flex items-center gap-2 shrink-0 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
          {reportBusy ? <RefreshCw size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Poslať prehľad teraz
        </button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 border border-white/10 bg-[#0d0d0d] px-4 py-3" data-testid="reconcile-bar">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-[#D4AF37]">Stripe platby:</span> ak platba prešla, ale klient sa nevrátil na stránku, tu ju <strong className="text-white/70">dorovnáte</strong> — faktúra sa pošle len pri <strong className="text-white/70">reálne uhradených</strong>. Zabráni to uviaznutým platbám.
        </p>
        <button onClick={reconcile} disabled={reconcileBusy} data-testid="reconcile-payments"
          className="inline-flex items-center gap-2 shrink-0 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
          {reconcileBusy ? <RotateCw size={15} className="animate-spin" /> : <RotateCw size={15} />} Overiť Stripe platby
        </button>
      </div>
      {reconcileMsg && <p className="text-[#D4AF37] text-sm mb-4" data-testid="reconcile-msg">{reconcileMsg}</p>}
      {reportMsg && <p className="text-[#D4AF37] text-sm mb-4" data-testid="accounting-report-msg">{reportMsg}</p>}
      {showNew && (
        <NewInvoiceForm token={token} onClose={() => setShowNew(false)} onCreated={fetchItems} />
      )}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoices-search"
          placeholder="Hľadať podľa čísla faktúry, odberateľa, e-mailu alebo IČO…"
          className="w-full bg-[#0a0a0a] border border-white/15 pl-10 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
      </div>
      <StateChips options={stateOpts} value={st} onChange={setSt} testid="invoices-state" />
      {items.length === 0 ? (
        <p className="text-white/40 text-center py-20" data-testid="invoices-empty">{loading ? "Načítavam…" : (q || st) ? "Žiadna faktúra nezodpovedá hľadaniu." : "Zatiaľ žiadne faktúry."}</p>
      ) : (
        <div className="space-y-4">
          {items.map((inv) => <InvoiceCard key={inv.id} inv={inv} token={token} onRemove={remove} onSave={save} onRefresh={fetchItems} />)}
        </div>
      )}
      <AdminPager page={list.page} pages={list.pages} total={list.total} onPage={list.setPage} testid="invoices-pager" />
    </div>
  );
}

function InvoiceCard({ inv, token, onRemove, onSave, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [copied, setCopied] = useState(false);

  const copyPayUrl = () => {
    navigator.clipboard.writeText(inv.pay_url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const downloadPdf = () => openPdf(`${API}/admin/invoices/${inv.id}/pdf`, { filename: `faktura-${inv.number}.pdf`, token });
  const previewInvoice = () => previewPdf(`${API}/admin/invoices/${inv.id}/pdf`, { token });

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const sendInvoice = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/send-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true); setTimeout(() => setSent(false), 3000);
    } catch { alert("E-mail sa nepodarilo odoslať."); }
    finally { setSending(false); }
  };
  const markPaid = async () => {
    if (!window.confirm("Označiť faktúru ako uhradenú bankovým prevodom?")) return;
    await onSave(inv.id, { status: "paid" });
  };

  const startEdit = () => {
    const d = {}; EDIT_FIELDS.forEach(([k]) => { d[k] = inv[k] ?? ""; }); setDraft(d); setEditing(true);
  };
  const save = async () => {
    const payload = { ...draft, amount_total: parseFloat(draft.amount_total) || inv.amount_total };
    if (await onSave(inv.id, payload)) setEditing(false);
  };

  return (
    <div data-testid="invoice-card" className="border border-white/10 bg-[#0a0a0a] p-5 md:p-6 hover:border-white/20 transition-colors">
      {editing ? (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {EDIT_FIELDS.map(([k, label]) => (
              <div key={k} className={k === "note" || k === "buyer_address" ? "md:col-span-2" : ""}>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">{label}</label>
                <input value={draft[k]} onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} data-testid="invoice-save" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-semibold hover:bg-[#F2E3C6]"><Save size={15} /> Uložiť</button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-white"><X size={15} /> Zrušiť</button>
          </div>
        </div>
      ) : (
        <>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <FileText className="text-[#D4AF37] mt-1" size={20} />
            <div>
              <p className="font-display text-lg leading-tight">{inv.corrects_number ? "Opravná faktúra" : "Faktúra"} č. {inv.number}</p>
              {inv.corrects_number && <p className="text-[#D4AF37]/80 text-xs mt-0.5" data-testid="invoice-corrects">nahrádza č. {inv.corrects_number}</p>}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-white/55">
                <span>{inv.buyer_name}</span>
                {inv.buyer_email && <button type="button" onClick={() => { try { navigator.clipboard.writeText(inv.buyer_email); } catch (e) {} }} title="Kliknutím skopírujete e-mail (neotvára osobný e-mail)" className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] cursor-pointer bg-transparent border-0 p-0 text-inherit"><Mail size={13} /> {inv.buyer_email}</button>}
              </div>
              <p className="text-white/35 text-xs mt-2">{fmtDate(inv.issued_at || inv.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[#D4AF37] font-semibold text-lg">{Number(inv.amount_total).toFixed(2)} €</p>
              <span className={`text-xs px-2 py-0.5 border ${inv.status === "paid" ? "border-green-500/50 text-green-400" : "border-white/20 text-white/50"}`}>
                {inv.status === "paid" ? "Zaplatená" : inv.status === "cancelled" ? `Zrušená${inv.corrected_by ? ` → č. ${inv.corrected_by}` : ""}` : inv.status}
              </span>
            </div>
            <button onClick={sendInvoice} disabled={sending} data-testid="invoice-send-email" className="text-white/40 hover:text-[#D4AF37] transition-colors disabled:opacity-50" title="Odoslať faktúru e-mailom">
              {sent ? <Check size={17} className="text-green-400" /> : <Mail size={17} />}
            </button>
            <button onClick={previewInvoice} data-testid="invoice-preview" className="text-white/40 hover:text-[#D4AF37] transition-colors" title="Náhľad PDF (otvoriť)"><Eye size={17} /></button>
            <button onClick={downloadPdf} data-testid="invoice-pdf" className="text-white/40 hover:text-[#D4AF37] transition-colors" title="Stiahnuť PDF"><Download size={17} /></button>
            <button onClick={startEdit} data-testid="invoice-edit" className="text-white/40 hover:text-[#D4AF37] transition-colors"><Edit3 size={17} /></button>
            <button onClick={() => onRemove(inv.id)} data-testid="invoice-delete" className="text-white/40 hover:text-red-400 transition-colors"><Trash2 size={17} /></button>
          </div>
        </div>
        {inv.status !== "paid" && inv.pay_url && (
          <div className="mt-4 flex items-center gap-2 border border-white/10 bg-[#111] p-2.5" data-testid="invoice-payurl">
            <Link2 size={15} className="text-white/40 shrink-0" />
            <input readOnly value={inv.pay_url} className="flex-1 bg-transparent text-white/70 text-xs focus:outline-none" data-testid="invoice-payurl-input" />
            <button onClick={copyPayUrl} data-testid="invoice-payurl-copy"
              className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-black px-3 py-1.5 text-xs font-semibold shrink-0">
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Skopírované" : "Kopírovať odkaz"}
            </button>
          </div>
        )}
        {inv.status !== "cancelled" && <InvoiceCorrect inv={inv} token={token} onDone={onRefresh} />}
        {inv.status !== "paid" && inv.status !== "cancelled" && (
          <div className="mt-3">
            <button onClick={markPaid} data-testid="invoice-mark-paid"
              className="inline-flex items-center gap-1.5 border border-green-500/40 text-green-400 px-3 py-1.5 text-xs hover:bg-green-500/10 transition-colors">
              <Check size={13} /> Označiť ako uhradené (prevod)
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
